import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import DashStatCard from '../../components/ui/DashStatCard'
import StatusBadge from '../../components/ui/StatusBadge'
import DashAvatar from '../../components/ui/DashAvatar'
import DashIcon from '../../components/ui/DashIcon'
import { usersAPI } from '../../api/users'
import { useUsers, useSuspendUser, useDeleteUser } from '../../hooks/useUsers'
import { useAdminStats } from '../../hooks/useAdminStats'
import ChangePasswordModal from '../../components/admin/ChangePasswordModal'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' })

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<'livreurs' | 'expediteurs' | 'admins'>('livreurs')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pwModal, setPwModal] = useState<{ _id: string; name: string } | null>(null)

  // Stats header
  const { data: stats } = useAdminStats('month')

  // Livreurs pending verification count
  const { data: pendingVerif } = useQuery({
    queryKey: ['livreurs-unverified-count'],
    queryFn: () => usersAPI.getLivreurs({ isVerified: false, limit: 1 }),
    staleTime: 60_000,
  })

  // Livreurs tab
  const { data: livreursData, isLoading: livreursLoading } = useQuery({
    queryKey: ['livreurs', { search, page }],
    queryFn: () => usersAPI.getLivreurs({ search: search || undefined, page, limit: 20 }),
    staleTime: 30_000,
    enabled: tab === 'livreurs',
  })

  // Expéditeurs tab — uses new generic useUsers hook
  const { data: expedData, isLoading: expedLoading } = useUsers(
    tab === 'expediteurs' ? { role: 'expediteur', search: search || undefined, page, limit: 20 } : {}
  )

  const suspendUser = useSuspendUser()
  const deleteUser = useDeleteUser()

  const livreurs = livreursData?.data?.data ?? []
  const livreursTotal = livreursData?.data?.total ?? 0
  const livreursTotalPages = livreursData?.data?.totalPages ?? 1

  const expediteurs = expedData?.users ?? []
  const expedTotal = expedData?.pagination.total ?? 0
  const expedTotalPages = expedData?.pagination.totalPages ?? 1

  const pendingCount = (pendingVerif as { data?: { total?: number } } | undefined)?.data?.total ?? 0

  const handleVerify = async (id: string) => {
    try {
      await usersAPI.verifyLivreur(id)
      toast.success('Livreur vérifié')
    } catch {
      toast.error('Erreur lors de la vérification')
    }
  }

  const handleDeactivate = async (id: string, name: string) => {
    if (!window.confirm(`Désactiver le compte de "${name}" ?`)) return
    try {
      await usersAPI.deactivateLivreur(id)
      toast.success('Compte désactivé')
    } catch {
      toast.error('Erreur lors de la désactivation')
    }
  }

  const handleSuspendExped = (id: string, isSuspended?: boolean, name?: string) => {
    if (isSuspended) {
      suspendUser.mutate({ id, suspended: false })
    } else {
      const reason = window.prompt(`Motif de suspension pour "${name}" :`)
      if (reason !== null) suspendUser.mutate({ id, suspended: true, reason })
    }
  }

  const tabs = [
    { id: 'livreurs' as const,    label: 'Livreurs',    count: stats?.livreurs.total ?? 0 },
    { id: 'expediteurs' as const, label: 'Expéditeurs', count: stats?.expediteurs.total ?? 0 },
    { id: 'admins' as const,      label: 'Admins',      count: 0 },
  ]

  const isLoading = tab === 'livreurs' ? livreursLoading : expedLoading

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Utilisateurs</h1>
          <p>Gérer livreurs, expéditeurs et administrateurs.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost"><DashIcon name="download" size={14} /> Exporter</button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin/expediteurs')}
          >
            <DashIcon name="plus" size={14} /> Gérer expéditeurs
          </button>
        </div>
      </div>

      <div className="stat-grid stat-grid-4" style={{ marginBottom: 18 }}>
        <DashStatCard
          label="Livreurs"
          value={(stats?.livreurs.total ?? 0).toLocaleString('fr-TN')}
          trend={`${stats?.livreurs.online ?? 0} en ligne`}
          icon="helmet"
        />
        <DashStatCard
          label="En ligne maintenant"
          value={(stats?.livreurs.online ?? 0).toLocaleString('fr-TN')}
          trend={stats?.livreurs.total ? `${Math.round((stats.livreurs.online / stats.livreurs.total) * 100)}% actifs` : '0%'}
          icon="zap"
          accent="green"
        />
        <DashStatCard
          label="Expéditeurs"
          value={(stats?.expediteurs.total ?? 0).toLocaleString('fr-TN')}
          trend="Comptes actifs"
          icon="users"
        />
        <DashStatCard
          label="Vérifications en attente"
          value={pendingCount.toLocaleString('fr-TN')}
          trend={pendingCount > 0 ? 'Action requise' : 'À jour'}
          icon="alert"
          accent={pendingCount > 0 ? 'dark' : undefined}
        />
      </div>

      <div className="tbl-card">
        <div className="tbl-toolbar">
          <div className="tbl-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                className={`tbl-tab${tab === t.id ? ' is-active' : ''}`}
                onClick={() => { setTab(t.id); setSearch(''); setPage(1) }}
              >
                {t.label}
                {t.count > 0 && <span className="count">{t.count.toLocaleString('fr-TN')}</span>}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          {tab !== 'admins' && (
            <div className="tbl-search">
              <DashIcon name="search" size={14} />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                placeholder="Nom, email, téléphone..."
              />
              {search && (
                <button
                  onClick={() => { setSearch(''); setPage(1) }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Livreurs ─────────────────────────────────────────────────────── */}
        {tab === 'livreurs' && (
          <div className="tbl-scroll">
            {isLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Chargement…</div>
            ) : livreurs.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
                {search ? `Aucun résultat pour "${search}"` : 'Aucun livreur'}
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Livreur</th>
                    <th>Téléphone</th>
                    <th>Statut</th>
                    <th>Note</th>
                    <th>Livraisons</th>
                    <th>Documents</th>
                    <th>Inscrit</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {livreurs.map((l: Record<string, unknown>) => (
                    <tr key={l._id as string}>
                      <td>
                        <div className="cell-user">
                          <DashAvatar name={l.name as string} size={32} />
                          <div>
                            <b>{l.name as string}</b>
                            <span>{l.phone as string}</span>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{l.phone as string}</td>
                      <td>
                        {l.isOnline
                          ? <span className="badge badge-green"><span className="pulse-dot" />En ligne</span>
                          : <span className="badge badge-navy">Hors ligne</span>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, color: 'var(--navy-900)' }}>
                          <DashIcon name="star" size={13} />
                          <span className="mono">{(l.rating as number)?.toFixed(1) ?? '—'}</span>
                        </div>
                      </td>
                      <td className="mono row-strong">{l.totalDeliveries as number ?? 0}</td>
                      <td>
                        {l.isVerified
                          ? <span className="badge badge-green">✓ Vérifié</span>
                          : <span className="badge badge-amber">⏳ En attente</span>
                        }
                      </td>
                      <td className="mono" style={{ color: 'var(--muted)', fontSize: '.82rem' }}>
                        {fmtDate(l.createdAt as string)}
                      </td>
                      <td className="col-actions">
                        <button>Voir</button>
                        {!(l.isVerified as boolean) && (
                          <button onClick={() => handleVerify(l._id as string)} style={{ color: '#10B981' }}>
                            Vérifier
                          </button>
                        )}
                        <button
                          onClick={() => setPwModal({ _id: l._id as string, name: l.name as string })}
                          style={{ color: '#7C3AED' }}
                        >
                          🔑 Mot de passe
                        </button>
                        <button
                          onClick={() => handleDeactivate(l._id as string, l.name as string)}
                          style={{ color: '#EF4444' }}
                        >
                          Désactiver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {livreursTotalPages > 1 && (
              <PaginationBar page={page} total={livreursTotalPages} onChange={setPage} totalItems={livreursTotal} />
            )}
          </div>
        )}

        {/* ── Expéditeurs ───────────────────────────────────────────────────── */}
        {tab === 'expediteurs' && (
          <div className="tbl-scroll">
            {isLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Chargement…</div>
            ) : expediteurs.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
                {search ? `Aucun résultat pour "${search}"` : 'Aucun expéditeur'}
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Expéditeur</th>
                    <th>Entreprise</th>
                    <th>Plan</th>
                    <th>Colis</th>
                    <th>Téléphone</th>
                    <th>Statut</th>
                    <th>Inscrit</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expediteurs.map((e) => (
                    <tr key={e._id}>
                      <td>
                        <div className="cell-user">
                          <DashAvatar name={e.company?.name ?? e.name} size={32} />
                          <div>
                            <b>{e.name}</b>
                            <span>{e.email ?? e.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="row-strong">{e.company?.name ?? '—'}</td>
                      <td>
                        {e.plan === 'pro'       && <span className="badge badge-blue">Pro</span>}
                        {e.plan === 'free'      && <span className="badge badge-navy">Free</span>}
                        {e.plan === 'business'  && <span className="badge badge-purple">Business</span>}
                        {!e.plan               && <span className="badge badge-navy">Free</span>}
                      </td>
                      <td className="mono row-strong">{e.parcelCount ?? 0}</td>
                      <td style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{e.phone}</td>
                      <td>
                        <StatusBadge status={e.isSuspended ? 'suspended' : 'active'} />
                      </td>
                      <td className="mono" style={{ color: 'var(--muted)', fontSize: '.82rem' }}>
                        {fmtDate(e.createdAt)}
                      </td>
                      <td className="col-actions">
                        <button onClick={() => navigate(`/admin/expediteurs/${e._id}`)}>Voir</button>
                        <button
                          onClick={() => setPwModal({ _id: e._id, name: e.name })}
                          style={{ color: '#7C3AED' }}
                        >
                          🔑 Mot de passe
                        </button>
                        <button
                          onClick={() => handleSuspendExped(e._id, e.isSuspended, e.name)}
                          style={{ color: e.isSuspended ? '#10B981' : '#EF4444' }}
                        >
                          {e.isSuspended ? 'Réactiver' : 'Suspendre'}
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Supprimer "${e.name}" ?`)) deleteUser.mutate(e._id)
                          }}
                          style={{ color: '#EF4444' }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {expedTotalPages > 1 && (
              <PaginationBar page={page} total={expedTotalPages} onChange={setPage} totalItems={expedTotal} />
            )}
          </div>
        )}

        {/* ── Admins ───────────────────────────────────────────────────────── */}
        {tab === 'admins' && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
            <DashIcon name="shield" size={32} />
            <p style={{ marginTop: 8 }}>Gestion des administrateurs disponible dans Paramètres → Équipe</p>
          </div>
        )}
      </div>

      {pwModal && (
        <ChangePasswordModal
          userId={pwModal._id}
          userName={pwModal.name}
          onClose={() => setPwModal(null)}
        />
      )}
    </>
  )
}

// ─── Pagination bar ───────────────────────────────────────────────────────────

function PaginationBar({ page, total, onChange, totalItems }: {
  page: number; total: number; onChange: (p: number) => void; totalItems: number
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--line)' }}>
      <button
        className="btn btn-sm btn-ghost"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
      >
        ← Précédent
      </button>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>
        Page {page} / {total} · {totalItems.toLocaleString('fr-TN')} utilisateurs
      </span>
      <button
        className="btn btn-sm btn-ghost"
        onClick={() => onChange(Math.min(total, page + 1))}
        disabled={page === total}
      >
        Suivant →
      </button>
    </div>
  )
}
